import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { processorName } from "../lib/invoice";
import { Link } from "react-router";
import { absoluteTime } from "../lib/format";
import { downloadInvoice, invoiceHtml } from "../lib/invoice";
import { formatMoney } from "../lib/money";
import type { BillingIdentity, BillingIssuer, Receipt } from "../lib/types";
import { Modal } from "./Modal";
import { Button } from "./ui";

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6">
      <dt className="text-sm text-fg-subtle">{label}</dt>
      <dd className="min-w-0 break-all text-left text-sm text-fg sm:text-right">{children}</dd>
    </div>
  );
}

export function ReceiptModal({
  receipt,
  onClose,
  issuer,
  customer,
  fallbackEmail,
}: {
  receipt: Receipt | null;
  onClose: () => void;
  /** Null when the deployment has no legal identity configured. */
  issuer: BillingIssuer | null;
  customer: BillingIdentity;
  fallbackEmail: string | null;
}) {
  const { t } = useTranslation("billing");
  /*
   * An invoice needs both parties, and either half can be missing for a
   * different reason: the operator has not configured theirs, or this
   * organization has not filled in its own. The two get different messages
   * because they have different remedies — one is a deployment setting nobody
   * here can change, the other is a form an owner can fill in now.
   */
  const customerReady = Boolean(customer.name?.trim() && customer.address?.trim());
  const canInvoice = issuer !== null && customerReady;

  return (
    <Modal
      open={receipt !== null}
      onClose={onClose}
      title={
        receipt ? t("receipt.title", { number: receipt.number }) : t("receipt.fallback")
      }
      description={t("receipt.lead")}
      footer={
        <>
          {receipt && canInvoice && (
            <Button
              variant="secondary"
              onClick={() =>
                downloadInvoice(
                  receipt,
                  invoiceHtml({ receipt, issuer: issuer!, customer, fallbackEmail }),
                )
              }
            >
              {t("receipt.download")}
              </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t("receipt.close")}
          </Button>
        </>
      }
    >
      {receipt && (
        <dl className="divide-y divide-line">
          <div className="py-3">
            <Row label={t("receipt.date")}>{absoluteTime(receipt.issuedAt)}</Row>
          </div>
          <div className="py-3">
            <Row label={t("receipt.amount")}>
              <span className="tabular-nums">{formatMoney(receipt.amountMicros, receipt.currency)}</span>
            </Row>
          </div>
          <div className="py-3">
            <Row label={t("receipt.paidVia")}>
              {/* Not CSS `capitalize`, which renders "Paypal" — tolerable on a
                  screen and not on a document somebody files. One spelling,
                  shared with the invoice this modal can download. */}
              <span>{processorName(receipt.provider)}</span>
            </Row>
          </div>
          <div className="py-3">
            <Row label={t("receipt.reference")}>
              <span className="ident break-all">{receipt.paymentId}</span>
            </Row>
          </div>
          <div className="py-3">
            <Row label={t("receipt.balanceAfter")}>
              {receipt.balanceAfterMicros === null ? (
                <span className="text-fg-subtle">{t("receipt.notRecorded")}</span>
              ) : (
                <span className="tabular-nums">{formatMoney(receipt.balanceAfterMicros, receipt.currency)}</span>
              )}
            </Row>
          </div>
        </dl>
      )}

      {/*
        Why there is no download, when there is not one. Two causes with two
        different remedies, so they get two messages — "unavailable" over a
        button somebody cannot fix is the version of this that generates a
        support ticket.
      */}
      {receipt && !canInvoice && (
        <p className="mt-4 border-t border-line pt-4 text-[13px] leading-6 text-fg-subtle">
          {!customerReady ? (
            <>
              Add a legal name and billing address in{" "}
              <Link
                to="/settings/billing-details"
                className="text-accent underline underline-offset-2"
              >
                {t("receipt.details")}
                </Link>{" "}
              to download this as an invoice.
            </>
          ) : (
            <>
              {t("receipt.noEntity")}
            </>
          )}
        </p>
      )}
    </Modal>
  );
}
